import { useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSearchSelect,
  FormSection,
} from "@NP-Webber/crm-kit";
import { shellApi, type ChatEligibleUserDto } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
  canSend: boolean;
};

export function CreateChatDialog({ open, onClose, onCreated, canSend }: Props) {
  const [kind, setKind] = useState<"direct" | "group">("direct");
  const [users, setUsers] = useState<ChatEligibleUserDto[]>([]);
  const [selected, setSelected] = useState<ChatEligibleUserDto[]>([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind("direct");
    setSelected([]);
    setName("");
    setError(null);
    void shellApi
      .listEligibleChatUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [open]);

  const available = useMemo(
    () => users.filter((u) => !selected.some((s) => s.userId === u.userId)),
    [users, selected],
  );

  const handleSave = async () => {
    if (!canSend || selected.length === 0) return;
    if (kind === "group" && selected.length < 2) {
      setError("בקבוצה יש לבחור לפחות שני משתתפים");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const item = await shellApi.createConversation({
        memberUserIds: selected.map((u) => u.userId),
        isGroup: kind === "group",
        name: kind === "group" ? name.trim() || undefined : undefined,
      });
      onCreated(item.id);
      onClose();
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog open={open} onClose={onClose} size="md">
      <FormCard
        title="שיחה חדשה"
        onSave={() => {
          void handleSave();
        }}
        onCancel={onClose}
        saveLabel="יצירה"
        cancelLabel="ביטול"
        saving={saving}
        saveDisabled={!canSend || selected.length === 0}
      >
        <FormSection>
          {error ? <FormAlert severity="error">{error}</FormAlert> : null}
          <FormGrid cols={1}>
            <div className="chat-create__kind">
              <button
                type="button"
                className={`chat-create__kind-btn${kind === "direct" ? " is-active" : ""}`}
                onClick={() => {
                  setKind("direct");
                  setSelected((prev) => prev.slice(0, 1));
                }}
              >
                ישיר
              </button>
              <button
                type="button"
                className={`chat-create__kind-btn${kind === "group" ? " is-active" : ""}`}
                onClick={() => setKind("group")}
              >
                קבוצה
              </button>
            </div>
            {kind === "group" ? (
              <FormField
                label="שם הקבוצה"
                value={name}
                onChange={setName}
                disabled={saving}
              />
            ) : null}
            <FormSearchSelect
              label={kind === "direct" ? "משתתף" : "הוספת משתתף"}
              options={available}
              value={null}
              onChange={(val: unknown) => {
                const user = val as ChatEligibleUserDto | null;
                if (!user?.userId) return;
                if (kind === "direct") {
                  setSelected([user]);
                } else {
                  setSelected((prev) =>
                    prev.some((s) => s.userId === user.userId)
                      ? prev
                      : [...prev, user],
                  );
                }
              }}
              getOptionLabel={(o) => (o as ChatEligibleUserDto)?.displayName || ""}
              isOptionEqual={(a, b) =>
                (a as ChatEligibleUserDto | null)?.userId ===
                (b as ChatEligibleUserDto | null)?.userId
              }
              placeholder="הקלידו לחיפוש משתמש…"
              disabled={saving}
            />
            {selected.length > 0 ? (
              <ul className="chat-create__selected">
                {selected.map((u) => (
                  <li key={u.userId}>
                    <span>{u.displayName}</span>
                    <button
                      type="button"
                      className="btn"
                      onClick={() =>
                        setSelected((prev) => prev.filter((x) => x.userId !== u.userId))
                      }
                    >
                      הסר
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </FormGrid>
        </FormSection>
      </FormCard>
    </FormDialog>
  );
}
